import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Search, Plus, Filter, Sparkles, Phone, Mail, MapPin, Calendar, User, ArrowRight, Target, IndianRupee, Upload, Loader2, FileSpreadsheet } from 'lucide-react';

const STAGE_CONFIG = {
  new: { label: 'New', bg: 'bg-blue-500', badge: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800' },
  quotation_sent: { label: 'Quotation Sent', bg: 'bg-violet-500', badge: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-800' },
  followup: { label: 'Follow-up', bg: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800' },
  negotiation: { label: 'Negotiation', bg: 'bg-orange-500', badge: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-800' },
  confirmed: { label: 'Confirmed', bg: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800' },
  lost: { label: 'Lost', bg: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800' }
};

const PRIORITY_CONFIG = {
  low: { badge: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300' },
  medium: { badge: 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300' },
  high: { badge: 'bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300' },
  urgent: { badge: 'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-300' }
};

const SOURCE_LABELS = { whatsapp: 'WhatsApp', email: 'Email', website: 'Website', manual: 'Manual' };
const DEPT_LABELS = { tours: 'Tours', visa: 'Visa', ticketing: 'Ticketing', domestic: 'Domestic', international: 'International' };

export default function LeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [importingLeads, setImportingLeads] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [newLead, setNewLead] = useState({
    client_name: '', phone: '', email: '', destination: '', travel_date: '',
    adults: 1, children: 0, budget: '', booking_type: '', source: 'manual', priority: 'medium', department: '', notes: ''
  });

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (stageFilter !== 'all') params.stage = stageFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (search) params.search = search;
      const res = await api.get('/leads', { params });
      let filtered = res.data;
      if (deptFilter !== 'all') {
        filtered = filtered.filter(l => l.department === deptFilter);
      }
      setLeads(filtered);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [stageFilter, sourceFilter, deptFilter, search]);

  const fetchAgents = useCallback(async () => {
    if (user?.role === 'manager' || user?.role === 'company_admin') {
      try {
        const res = await api.get('/users', { params: { role: 'agent' } });
        setAgents(res.data);
      } catch (err) { toast.error('Failed to load agents'); }
    }
  }, [user]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newLead.client_name) { toast.error('Client name is required'); return; }
    try {
      await api.post('/leads', newLead);
      toast.success('Lead created');
      setShowCreate(false);
      setNewLead({ client_name: '', phone: '', email: '', destination: '', travel_date: '', adults: 1, children: 0, budget: '', booking_type: '', source: 'manual', priority: 'medium', department: '', notes: '' });
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleAIExtract = async () => {
    if (!aiText.trim()) { toast.error('Enter enquiry text'); return; }
    setAiLoading(true);
    try {
      const res = await api.post('/leads/ai-extract', { raw_text: aiText, source: 'manual' });
      const data = res.data.extracted_data;
      if (data && Object.keys(data).length > 0) {
        setNewLead(prev => ({
          ...prev,
          client_name: data.client_name || prev.client_name,
          phone: data.phone || prev.phone,
          email: data.email || prev.email,
          destination: data.destination || prev.destination,
          travel_date: data.travel_date || prev.travel_date,
          adults: data.adults || prev.adults,
          children: data.children || prev.children,
          budget: data.budget || prev.budget,
          booking_type: data.booking_type || prev.booking_type,
          priority: data.priority || prev.priority,
          raw_message: aiText
        }));
        toast.success('Data extracted successfully');
        setShowAI(false);
        setShowCreate(true);
      } else {
        toast.error(res.data.error || 'Could not extract data');
      }
    } catch (err) {
      toast.error('AI extraction failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingLeads(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/leads/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportingLeads(false);
      e.target.value = '';
    }
  };


  const handleAssign = async (leadId, agentId) => {
    try {
      await api.post(`/leads/${leadId}/assign`, { agent_id: agentId });
      toast.success('Lead assigned');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">
            {user?.role === 'agent' ? 'My Leads' : 'Leads'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{leads.length} total leads</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showAI} onOpenChange={setShowAI}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2 border-voyage-orange/40 text-voyage-orange hover:bg-voyage-orange/10 hover:border-voyage-orange" data-testid="ai-extract-btn">
                <Sparkles className="w-4 h-4" /> AI Extract
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-voyage-orange" /> AI Lead Extraction
                </DialogTitle>
                <DialogDescription>Paste unstructured enquiry text to auto-extract lead data using AI.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm">Paste enquiry text (WhatsApp, Email, etc.)</Label>
                  <Textarea
                    value={aiText}
                    onChange={(e) => setAiText(e.target.value)}
                    placeholder="Hi, I want to book a trip to Bali for 2 adults and 1 child (5 years). Budget around 2 lakh. Travel date 15th March. My name is Rahul, phone 9876543210."
                    rows={6}
                    className="mt-1.5"
                    data-testid="ai-text-input"
                  />
                </div>
                <Button onClick={handleAIExtract} disabled={aiLoading} className="w-full gradient-orange hover:opacity-90 text-white" data-testid="ai-extract-submit-btn">
                  {aiLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Extract Data'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          {user?.role !== 'agent' && (
            <>
            <label className="cursor-pointer">
              <Button variant="outline" className="gap-2" disabled={importingLeads} asChild>
                <span data-testid="import-leads-btn">
                  {importingLeads ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                  Import CSV/Excel
                </span>
              </Button>
              <input type="file" accept=".csv,.xlsx,.xls" onChange={handleBulkImport} className="hidden" data-testid="import-file-input" />
            </label>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button className="gap-2 gradient-blue hover:opacity-90 text-white shadow-md shadow-voyage-blue/20" data-testid="create-lead-btn">
                  <Plus className="w-4 h-4" /> New Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create New Lead</DialogTitle>
                <DialogDescription>Fill in the lead details to create a new enquiry.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4" data-testid="create-lead-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label className="text-sm">Client Name *</Label><Input value={newLead.client_name} onChange={e => setNewLead({...newLead, client_name: e.target.value})} className="mt-1" data-testid="lead-name-input" /></div>
                    <div><Label className="text-sm">Phone</Label><Input value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} className="mt-1" data-testid="lead-phone-input" /></div>
                    <div><Label className="text-sm">Email</Label><Input value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} className="mt-1" data-testid="lead-email-input" /></div>
                    <div><Label className="text-sm">Destination</Label><Input value={newLead.destination} onChange={e => setNewLead({...newLead, destination: e.target.value})} className="mt-1" data-testid="lead-destination-input" /></div>
                    <div><Label className="text-sm">Travel Date</Label><Input type="date" value={newLead.travel_date} onChange={e => setNewLead({...newLead, travel_date: e.target.value})} className="mt-1" data-testid="lead-date-input" /></div>
                    <div><Label className="text-sm">Budget</Label><Input value={newLead.budget} onChange={e => setNewLead({...newLead, budget: e.target.value})} className="mt-1" data-testid="lead-budget-input" /></div>
                    <div><Label className="text-sm">Adults</Label><Input type="number" min={1} value={newLead.adults} onChange={e => setNewLead({...newLead, adults: parseInt(e.target.value) || 1})} className="mt-1" /></div>
                    <div><Label className="text-sm">Children</Label><Input type="number" min={0} value={newLead.children} onChange={e => setNewLead({...newLead, children: parseInt(e.target.value) || 0})} className="mt-1" /></div>
                    <div>
                      <Label className="text-sm">Booking Type</Label>
                      <Select value={newLead.booking_type} onValueChange={v => setNewLead({...newLead, booking_type: v})}>
                        <SelectTrigger className="mt-1" data-testid="lead-booking-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="package">Package (Tour)</SelectItem>
                          <SelectItem value="hotel">Hotel Only</SelectItem>
                          <SelectItem value="flight">Flight Only</SelectItem>
                          <SelectItem value="visa">Visa Only</SelectItem>
                          <SelectItem value="flight_hotel">Flight + Hotel</SelectItem>
                          <SelectItem value="ticket">Event/Train Ticket</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Source</Label>
                      <Select value={newLead.source} onValueChange={v => setNewLead({...newLead, source: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual</SelectItem>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="website">Website</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Priority</Label>
                      <Select value={newLead.priority} onValueChange={v => setNewLead({...newLead, priority: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Department</Label>
                      <Select value={newLead.department} onValueChange={v => setNewLead({...newLead, department: v})}>
                        <SelectTrigger className="mt-1" data-testid="lead-department-select"><SelectValue placeholder="Select dept" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tours">Tours</SelectItem>
                          <SelectItem value="visa">Visa</SelectItem>
                          <SelectItem value="ticketing">Ticketing</SelectItem>
                          <SelectItem value="domestic">Domestic</SelectItem>
                          <SelectItem value="international">International</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="text-sm">Notes</Label><Textarea value={newLead.notes} onChange={e => setNewLead({...newLead, notes: e.target.value})} className="mt-1" rows={3} /></div>
                  <Button type="submit" className="w-full gradient-blue hover:opacity-90 text-white" data-testid="submit-lead-btn">Create Lead</Button>
                </form>
              </DialogContent>
            </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-10"
            data-testid="leads-search-input"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-full sm:w-44 h-10" data-testid="stage-filter">
            <Filter className="w-3.5 h-3.5 mr-1.5" /><SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="quotation_sent">Quotation Sent</SelectItem>
            <SelectItem value="followup">Follow-up</SelectItem>
            <SelectItem value="negotiation">Negotiation</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full sm:w-40 h-10" data-testid="source-filter">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="website">Website</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-full sm:w-44 h-10" data-testid="dept-filter">
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            <SelectItem value="tours">Tours</SelectItem>
            <SelectItem value="visa">Visa</SelectItem>
            <SelectItem value="ticketing">Ticketing</SelectItem>
            <SelectItem value="domestic">Domestic</SelectItem>
            <SelectItem value="international">International</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leads List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : leads.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Target className="w-7 h-7 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium">No leads found</p>
            <p className="text-xs text-muted-foreground mt-1">Create a new lead or adjust your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="leads-list">
          {leads.map((lead) => {
            const stageConf = STAGE_CONFIG[lead.stage] || STAGE_CONFIG.new;
            const prioConf = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.medium;
            return (
              <Card
                key={lead.id}
                className="border border-border bg-card card-hover-lift cursor-pointer overflow-hidden"
                data-testid={`lead-card-${lead.id}`}
              >
                <CardContent className="p-0" onClick={() => navigate(`/dashboard/leads/${lead.id}`)}>
                  <div className="flex">
                    {/* Colored left stripe */}
                    <div className={`w-1 flex-shrink-0 ${stageConf.bg}`} />
                    <div className="flex-1 p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-heading font-semibold text-foreground">{lead.client_name || 'Unknown'}</span>
                            <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">{lead.inquiry_id}</span>
                            <Badge className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium border ${stageConf.badge}`}>
                              {stageConf.label}
                            </Badge>
                            <Badge className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium border ${prioConf.badge}`}>
                              {lead.priority}
                            </Badge>
                            {lead.department && (
                              <Badge className="rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800 capitalize">
                                {DEPT_LABELS[lead.department] || lead.department}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                            {lead.destination && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-voyage-blue" />{lead.destination}</span>}
                            {lead.travel_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-violet-500" />{lead.travel_date}</span>}
                            {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-emerald-500" />{lead.phone}</span>}
                            {lead.budget && <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3 text-voyage-orange" />{lead.budget}</span>}
                            <span className="capitalize text-muted-foreground/70">{SOURCE_LABELS[lead.source] || lead.source}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {(user?.role === 'manager' || user?.role === 'company_admin') && !lead.assigned_to && (
                            <Select onValueChange={(agentId) => { handleAssign(lead.id, agentId); }} >
                              <SelectTrigger className="w-36 h-8 text-xs" data-testid={`assign-select-${lead.id}`}>
                                <User className="w-3 h-3 mr-1" /><SelectValue placeholder="Assign" />
                              </SelectTrigger>
                              <SelectContent>
                                {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                          {lead.assigned_to && (
                            <Badge className="rounded-full text-[10px] bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
                              Assigned
                            </Badge>
                          )}
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
